import React, { useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import * as d3 from 'd3-force';
import {
  getNodeColor, getAdvisorInitials, contactAdvisors, peopleLabel, targetAccountLabel, managerNames, OUTREACH_STAGE_LABELS
} from './utils';
import { getCanvasIcon, preloadCanvasIcons } from '../utils/canvasIcons';
import './GraphView.css';

// Icons drawn on the canvas (public/icons/*.svg)
const COMPANY_ICON = 'building';
const INDUSTRY_ICON = 'factory';
const ICP_ICON = 'target';
preloadCanvasIcons([COMPANY_ICON, INDUSTRY_ICON, ICP_ICON]);

// Nodes outside the hovered node's neighbourhood fade to this alpha.
const FOCUS_DIM_ALPHA = 0.12;
// Fraction of the remaining distance covered per frame (higher = snappier).
const EASE = 0.18;

// On-screen size (px) follows an S-curve in log-zoom: it eases from `min` when zoomed
// out to `max` when zoomed in, so nodes and edges never vanish or balloon at the extremes.
const SIZE_STEEPNESS = 1.6;
const NODE_SCREEN_RADIUS = { min: 4, max: 22 }; // px, for a node whose base size is NODE_REF_SIZE
const EDGE_SCREEN_WIDTH = { min: 0.5, max: 3 };
const NODE_REF_SIZE = 20;

// Labels are drawn LABEL_PX big at zoom 1, and never outside these limits on screen
const LABEL_PX = 15;
const LABEL_MIN_PX = 14;
const LABEL_MAX_PX = 19;
// How wide a label gets before it wraps, in characters of its own size
const LABEL_WRAP_EMS = 11;
// Room kept round a company for its label, in world units per character
const LABEL_ROOM_PER_CHARACTER = 3.6;
const LABEL_ROOM_CHARACTERS = 16;
const LABEL_PADDING_PX = 3;
// Which labels get the room when there isn't enough for all of them; hubs always show
const LABEL_PRIORITY = { industry: 2, more_companies: 1 };
const LABEL_ALWAYS = new Set(['industry']);
const FONT_STACK = 'Inter, "SF Pro Text", -apple-system, sans-serif';

const sigmoid = (x) => 1 / (1 + Math.exp(-x));

// Contact-count bubbles scale by log(count), so one huge company doesn't flatten every other
// node to the same max radius. Real per-company counts are heavily right-skewed now that
// LinkedIn-imported connections count too: most companies have 1-3 contacts, but a handful (e.g.
// Synechron, Wells Fargo) run into the hundreds. A linear or sqrt curve puts all of those
// outliers at the same clamped ceiling, which is the bug this replaces - log keeps compressing
// instead of flattening, so 21 vs 141 vs 378 still read as different sizes.
const COUNT_NODE_MIN_RADIUS = 10;
const COUNT_NODE_MAX_RADIUS = 22;
// Comfortably above today's real max (378, see li-contact-data-caveats memory) so the biggest
// company today isn't already pinned to the ceiling; re-check if growth pushes past ~1000.
const COUNT_NODE_REFERENCE = 1000;
const contactCountRadius = (count) => {
  const t = Math.min(Math.log(Math.max(count, 0) + 1) / Math.log(COUNT_NODE_REFERENCE + 1), 1);
  return COUNT_NODE_MIN_RADIUS + (COUNT_NODE_MAX_RADIUS - COUNT_NODE_MIN_RADIUS) * t;
};

const curvePx = (range, zoom) =>
  range.min + (range.max - range.min) * sigmoid(SIZE_STEEPNESS * Math.log(Math.max(zoom, 1e-6)));

// Factor that scales world-space drawing so a node of base size NODE_REF_SIZE is curvePx() big on screen.
const nodeScale = (zoom) => curvePx(NODE_SCREEN_RADIUS, zoom) / (NODE_REF_SIZE * zoom);

// Edge width in world units (the renderer multiplies by zoom, so divide the target px by it).
const edgeWorldWidth = (zoom, highlight = 0) => (curvePx(EDGE_SCREEN_WIDTH, zoom) * (1 + highlight * 1.5)) / zoom;

// Base node size (world units), used for click areas and relative sizing.
const nodeBaseSize = (node) => {
  switch (node.group) {
    case 'contact': return 10;
    case 'company': return 20;
    case 'more_companies': return 14;
    case 'contact_count': return contactCountRadius(node.contactCountData?.count || 1);
    case 'industry': return 25;
    default: return 20;
  }
};

const endpointId = (end) => (end && typeof end === 'object' ? end.id : end);

// Exponential ease toward `target`, stored on the object so it persists across frames.
const easeToward = (obj, key, target) => {
  const current = obj[key] ?? target;
  obj[key] = current + (target - current) * EASE;
  return obj[key];
};

const easeNodeAlpha = (node, focus) =>
  easeToward(node, '__alpha', !focus || focus.nodes.has(node.id) ? 1 : FOCUS_DIM_ALPHA);

// Links fade to this alpha when they are not between two focused nodes.
const FOCUS_LINK_DIM_ALPHA = 0.08;

// Apply an alpha to a #rgb / #rrggbb colour. Other formats are returned unchanged.
const withAlpha = (color, alpha) => {
  const match = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(String(color || '').trim());
  if (!match) return color;
  let hex = match[1];
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const n = parseInt(hex, 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const getCSSVariable = (variable) =>
  getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

// Space (px) kept around the graph when the camera fits it
const FIT_PADDING = 100;
// How long the camera takes to make room beside a company's panel
const PANEL_MOVE_MS = 400;

// People are cards with their name inside. The name is PERSON_FONT world units (15px at zoom 1)
// but never drawn smaller than PERSON_MIN_PX or larger than PERSON_MAX_PX on screen.
const PERSON_FONT = 15;
const PERSON_MIN_PX = 14;
const PERSON_MAX_PX = 20;
// Names longer than this many font sizes are cut with an ellipsis
const PERSON_MAX_TEXT = 14;
// Font size used to space the cards apart, a little above PERSON_FONT for some margin
const PERSON_SPACING_FONT = 17;

const personName = (contact) =>
  `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `Contact ${contact.id}`;

// The card's text and rectangle in world units at this zoom, cached on the node until either changes
const personCard = (ctx, node, zoom) => {
  const fontPx = Math.min(PERSON_MAX_PX, Math.max(PERSON_MIN_PX, PERSON_FONT * zoom));
  const font = fontPx / zoom;
  const name = personName(node.contactData);
  const cached = node.__card;
  if (cached && cached.font === font && cached.name === name) {
    return { ...cached, x: node.x - cached.width / 2, y: node.y - cached.height / 2 };
  }
  ctx.font = `600 ${font}px Inter, "SF Pro Text", -apple-system, sans-serif`;
  let text = name;
  if (ctx.measureText(text).width > font * PERSON_MAX_TEXT) {
    while (text.length > 1 && ctx.measureText(`${text}…`).width > font * PERSON_MAX_TEXT) text = text.slice(0, -1);
    text = `${text.trimEnd()}…`;
  }
  const width = ctx.measureText(text).width + font * 1.5;
  const height = font * 1.9;
  node.__card = { name, font, text, width, height };
  return { ...node.__card, x: node.x - width / 2, y: node.y - height / 2 };
};

const wrapLabel = (ctx, text, maxWidth) => {
  const lines = [];
  let line = '';
  text.split(' ').forEach((word) => {
    const longer = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(longer).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = longer;
    }
  });
  return line ? [...lines, line] : lines;
};

// Draws the frame's labels, the ones that matter most first, and leaves out any that would land on
// one already drawn. Text stays one readable size, so a crowded view shows fewer labels, not a
// pile of them, and zooming in brings the rest. Hubs and whatever is under the pointer always show.
const drawLabels = (ctx, labels, zoom, palette) => {
  const font = Math.min(LABEL_MAX_PX, Math.max(LABEL_MIN_PX, LABEL_PX * zoom)) / zoom;
  const lineHeight = font * 1.2;
  const padding = LABEL_PADDING_PX / zoom;
  const placed = [];
  ctx.save();
  ctx.font = `500 ${font}px ${FONT_STACK}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  [...labels].sort((a, b) => b.priority - a.priority || String(a.id).localeCompare(String(b.id))).forEach((label) => {
    const lines = label.wrap ? wrapLabel(ctx, label.text, font * LABEL_WRAP_EMS) : [label.text];
    const width = Math.max(...lines.map((line) => ctx.measureText(line).width));
    const box = {
      left: label.x - width / 2 - padding,
      right: label.x + width / 2 + padding,
      top: label.y - padding / 2,
      bottom: label.y + lines.length * lineHeight + padding / 2
    };
    const taken = placed.some((other) =>
      box.left < other.right && other.left < box.right && box.top < other.bottom && other.top < box.bottom);
    // A label that's drawn can be clicked (nodePointerAreaPaint); one that's left out can't
    label.node.__labelBox = taken && !label.always ? null : box;
    if (!label.node.__labelBox) return;
    placed.push(box);
    ctx.globalAlpha = label.alpha;
    ctx.fillStyle = palette.panelTranslucent;
    ctx.beginPath();
    ctx.roundRect(box.left, box.top, box.right - box.left, box.bottom - box.top, 4 / zoom);
    ctx.fill();
    ctx.fillStyle = palette.text;
    lines.forEach((line, index) => ctx.fillText(line, label.x, label.y + index * lineHeight));
  });
  ctx.restore();
};

// How far apart companies are kept, so their labels fit
const labelRoom = (node) =>
  Math.min((node.name || '').length, LABEL_ROOM_CHARACTERS) * LABEL_ROOM_PER_CHARACTER + nodeBaseSize(node);

// How far apart people are kept: about half a card's width at PERSON_SPACING_FONT
const personSpacing = (node) => {
  const length = Math.min(personName(node.contactData).length, PERSON_MAX_TEXT * 1.8);
  return (length * PERSON_SPACING_FONT * 0.56 + PERSON_SPACING_FONT * 1.5) / 2 + 6;
};

// Moves the camera onto the whole graph. With keepZoom (a pinned zoom level) it only centres it.
// hiddenEachSide is how much of the canvas is out of sight on each side, beside a company's panel.
const showWholeGraph = (fg, durationMs, keepZoom, hiddenEachSide = 0) => {
  if (!fg) return;
  if (!keepZoom) {
    fg.zoomToFit(durationMs, FIT_PADDING + hiddenEachSide);
    return;
  }
  const box = fg.getGraphBbox();
  if (box) fg.centerAt((box.x[0] + box.x[1]) / 2, (box.y[0] + box.y[1]) / 2, durationMs);
};

// Moves the camera onto a company and what's linked to it, in the room beside its panel. The middle
// of the canvas is already the middle of that room, and it only zooms out, when they wouldn't fit.
const showBesidePanel = (fg, neighbours, room, height, keepZoom) => {
  const box = fg?.getGraphBbox((node) => neighbours.has(node.id));
  if (!box) return;
  fg.centerAt((box.x[0] + box.x[1]) / 2, (box.y[0] + box.y[1]) / 2, PANEL_MOVE_MS);
  const fits = Math.min(
    (room - FIT_PADDING * 2) / (box.x[1] - box.x[0]),
    (height - FIT_PADDING * 2) / (box.y[1] - box.y[0])
  );
  if (!keepZoom && fits < fg.zoom()) fg.zoom(fits, PANEL_MOVE_MS);
};

// Hover text for a node
const nodeTooltip = (node) => {
  switch (node.group) {
    case 'contact': {
      const contact = node.contactData || {};
      const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `Contact ${contact.id}`;
      const title = contact.position?.name || contact.position_name || contact.title;
      const advisors = contactAdvisors(contact);
      return [
        name,
        title,
        advisors.length ? `Known by ${advisors.join(', ')}` : null,
        contact.target_role ? `${contact.target_role === 'to' ? 'Main' : 'CC'} contact on a target account` : null
      ].filter(Boolean).join('\n');
    }
    case 'contact_count':
      return `${peopleLabel(node.contactCountData?.count || 0)} at ${node.contactCountData?.company?.name || 'this company'} - click to see them`;
    case 'more_companies':
      return `${node.name} - click to browse them in the Tree view`;
    case 'company': {
      const data = node.companyData || {};
      const account = data.target_account;
      return [
        node.name,
        data.company_name && data.company_name !== node.name ? `(${data.company_name})` : null,
        account ? targetAccountLabel(account) : null,
        // Target accounts name their managers in the line above
        !account && data.relationship_managers?.length ? `Managed by ${managerNames(data.relationship_managers)}` : null,
        account?.outreach_stage ? OUTREACH_STAGE_LABELS[account.outreach_stage] : null,
        data.network_people ? `Advisors know ${peopleLabel(data.network_people)} here` : null,
        'Click for details'
      ].filter(Boolean).join('\n');
    }
    default:
      return node.name || node.id;
  }
};

const GraphView = ({
  graphRef,
  graphData,
  windowDimensions,
  theme,
  selectedNodeData,
  onNodeClick,
  onZoomChange,
  pinnedZoom,
  companyInPanel
}) => {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [isSimulationStable, setIsSimulationStable] = useState(false);
  // Current zoom, read by link widths in the draw loop (a ref, so zooming does not re-render)
  const zoomRef = useRef(1);
  const zoomFrame = useRef(null);

  // Theme colours, read once per theme rather than on every frame
  const palette = useMemo(() => ({
    background: getCSSVariable('--background'),
    border: getCSSVariable('--border'),
    borderStrong: getCSSVariable('--border-color'),
    panel: getCSSVariable('--panel'),
    panelTranslucent: getCSSVariable('--panel-translucent'),
    text: getCSSVariable('--text'),
    link: getCSSVariable('--link'),
    company: getCSSVariable('--graph-node-company'),
    contact: getCSSVariable('--graph-node-contact')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [theme]);

  // One company's people (the drill-down): its name is drawn as legibly as theirs
  const peopleShown = useMemo(() => graphData.nodes.some((node) => node.group === 'contact'), [graphData]);

  // Neighbour lookup (node id -> Set of connected ids, including itself) for hover focus.
  const neighbourIndex = useMemo(() => {
    const index = new Map();
    const link = (a, b) => {
      if (!index.has(a)) index.set(a, new Set([a]));
      index.get(a).add(b);
    };
    (graphData?.links || []).forEach((l) => {
      const s = endpointId(l.source);
      const t = endpointId(l.target);
      link(s, t);
      link(t, s);
    });
    return index;
  }, [graphData]);

  // Read from inside the canvas draw loop, so it is a ref rather than state.
  const focusRef = useRef(null);
  const labelQueue = useRef([]);
  focusRef.current = hoveredNode && neighbourIndex.has(hoveredNode.id)
    ? { nodes: neighbourIndex.get(hoveredNode.id) }
    : hoveredNode
      ? { nodes: new Set([hoveredNode.id]) }
      : null;

  // Company logos by node id; null marks a logo that failed to load
  const [companyLogos, setCompanyLogos] = useState(() => new Map());
  const requestedLogos = useRef(new Set());

  useEffect(() => {
    graphData.nodes.forEach((node) => {
      if (node.group !== 'company' || requestedLogos.current.has(node.id)) return;

      const logoUuid = node.companyData?.logo_uuid;
      const logoUrl = logoUuid
        ? `${process.env.REACT_APP_API_BASE_URL || ''}/api/v2/logos/${logoUuid}`
        : node.companyData?.logoUrl;
      if (!logoUrl) return;

      requestedLogos.current.add(node.id);
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS for external images
      img.onload = () => setCompanyLogos(prev => new Map(prev).set(node.id, img));
      img.onerror = () => setCompanyLogos(prev => new Map(prev).set(node.id, null));
      img.src = logoUrl;
    });
  }, [graphData.nodes]);

  // Nodes stay hidden until the forces below are set up for the first dataset
  const [shouldShowNodes, setShouldShowNodes] = useState(false);

  // Each new dataset (drilling into a company, going back, another page) is laid out afresh around the
  // origin while the camera is still where the last one left it, often centred on a clicked node far
  // away, which shows an empty canvas. So bring the whole graph into view: at once on the first engine
  // tick (the warm-up layout is ready by then), and smoothly again once the layout settles.
  const fitStage = useRef(null); // 'first-tick', then 'settled', then null
  const keepZoom = useRef(false);
  keepZoom.current = Boolean(pinnedZoom);

  useEffect(() => {
    if (!graphData.nodes.length) return;
    setIsSimulationStable(false);
    fitStage.current = 'first-tick';
  }, [graphData]);

  // A company's panel covers the right of the canvas, so the graph slides over by half its width
  // (--room in CompanyDetailPanel.css) and the camera works within the room left. On a phone
  // the panel takes the whole window, and there's nothing to fit beside it.
  const hiddenEachSide = useRef(0);

  useEffect(() => {
    const panelWidth = companyInPanel ? document.querySelector('.company-detail-panel')?.offsetWidth || 0 : 0;
    const room = windowDimensions.width - panelWidth;
    hiddenEachSide.current = room > FIT_PADDING * 2 ? panelWidth / 2 : 0;
    // Opened from anywhere else (a person's details, Ask Sinatra), it isn't the node itself
    const node = companyInPanel?.x != null ? companyInPanel : companyInPanel && graphData.nodes.find(
      (other) => other.group === 'company' && other.companyData?.id === companyInPanel.companyData?.id);
    if (hiddenEachSide.current && node?.x != null) {
      const neighbours = neighbourIndex.get(node.id) || new Set([node.id]);
      showBesidePanel(graphRef.current, neighbours, room, windowDimensions.height, keepZoom.current);
    }
  // Only when a panel opens or shows another company: a new dataset is fitted as it settles
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInPanel]);

  useEffect(() => {
    if (graphRef?.current && graphData.nodes.length > 0) {
      const fg = graphRef.current;

      setTimeout(() => {
        if (fg.d3Force) {
          const chargeForce = fg.d3Force('charge');
          if (chargeForce) {
            chargeForce.strength(-200);
          }

          const linkForce = fg.d3Force('link');
          if (linkForce) {
            linkForce.distance(150).strength(0.3);
          }

          const centerForce = fg.d3Force('center');
          if (!centerForce) {
            fg.d3Force('center', d3.forceCenter(0, 0).strength(0.02));
          } else {
            centerForce.strength(0.02);
          }

          const componentSeparationForce = d3.forceRadial()
            .radius((node) => {
              if (node.group === 'company') {
                return 150;
              }
              return 80;
            })
            .x(0)
            .y(0)
            .strength(0.05);

          fg.d3Force('componentSeparation', componentSeparationForce);

          const companyRepulsionForce = d3.forceManyBody()
            .strength((node) => {
              if (node.group === 'industry') {
                return -800;
              }
              return node.group === 'company' ? -500 : -80;
            })
            .distanceMin(80)
            .distanceMax(300);

          fg.d3Force('companyRepulsion', companyRepulsionForce);

          // People's cards and companies' labels push each other apart so the names don't overlap
          fg.d3Force('personCollide', d3.forceCollide()
            .radius((node) => {
              if (node.group === 'contact' && node.contactData) return personSpacing(node);
              return node.group === 'company' ? labelRoom(node) : 0;
            })
            .strength(0.9));
        }

        setTimeout(() => {
          setShouldShowNodes(true);
        }, 300);
      }, 100);
    }
  }, [graphData, graphRef]);

  useEffect(() => {
    return () => {
      document.body.style.cursor = 'auto';
      if (zoomFrame.current !== null) cancelAnimationFrame(zoomFrame.current);
    };
  }, []);

  // A person: a rounded card in the person colour with their name inside. The badge on its corner
  // shows the first advisor's initials (or TO/CC for list people nobody knows); several advisors add
  // a gold outline and "+N".
  const drawPersonCard = (ctx, node, zoom, hovered, selected) => {
    const card = personCard(ctx, node, zoom);
    const px = 1 / zoom;
    const radius = card.height * 0.32;
    const advisors = contactAdvisors(node.contactData);

    ctx.fillStyle = palette.contact;
    ctx.beginPath();
    ctx.roundRect(card.x, card.y, card.width, card.height, radius);
    ctx.fill();
    if (hovered) {
      ctx.fillStyle = theme === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(255, 255, 255, 0.16)';
      ctx.fill();
    }

    ctx.fillStyle = theme === 'dark' ? palette.background : '#ffffff';
    ctx.font = `600 ${card.font}px Inter, "SF Pro Text", -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(card.text, node.x, node.y + card.font * 0.04);

    if (advisors.length > 1) {
      ctx.strokeStyle = theme === 'dark' ? '#ffd700' : '#c77700';
      ctx.lineWidth = 2 * px;
      ctx.beginPath();
      ctx.roundRect(card.x - 3 * px, card.y - 3 * px, card.width + 6 * px, card.height + 6 * px, radius + 3 * px);
      ctx.stroke();
    }
    if (selected) {
      ctx.strokeStyle = palette.link;
      ctx.lineWidth = 2.5 * px;
      ctx.beginPath();
      ctx.roundRect(card.x - 5 * px, card.y - 5 * px, card.width + 10 * px, card.height + 10 * px, radius + 5 * px);
      ctx.stroke();
    }

    const badgeText = advisors.length
      ? getAdvisorInitials(advisors[0])
      : (node.contactData.target_role || '').toUpperCase();
    if (!badgeText) return;
    const badgeRadius = card.height * 0.36;
    const badgeX = card.x + card.width - badgeRadius * 0.35;
    const badgeY = card.y + badgeRadius * 0.15;
    ctx.fillStyle = palette.panel;
    ctx.strokeStyle = palette.contact;
    ctx.lineWidth = 1.5 * px;
    ctx.beginPath();
    ctx.arc(badgeX, badgeY, badgeRadius, 0, 2 * Math.PI, false);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = palette.text;
    ctx.font = `700 ${badgeRadius * 0.85}px Inter, "SF Pro Text", -apple-system, sans-serif`;
    ctx.fillText(badgeText, badgeX, badgeY + badgeRadius * 0.05);
    if (advisors.length > 1) {
      ctx.fillStyle = theme === 'dark' ? '#ffd700' : '#c77700';
      ctx.font = `700 ${badgeRadius * 0.8}px Inter, "SF Pro Text", -apple-system, sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillText(`+${advisors.length - 1}`, badgeX, badgeY + badgeRadius + 1 * px);
    }
  };

  // Draws `iconName` centred on the node, tinted `color`, if the icon file has loaded
  const drawIcon = (ctx, iconName, color, x, y, size) => {
    const icon = getCanvasIcon(iconName, color);
    if (icon) {
      ctx.drawImage(icon, x - size / 2, y - size / 2, size, size);
    }
  };

  return (
    <div className="graph-view-container">
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        width={windowDimensions.width}
        height={windowDimensions.height}
        backgroundColor={palette.background}
        nodeAutoColorBy="group"
        minZoom={0.2}
        maxZoom={4}
        enableZoomInteraction={!pinnedZoom}
        enablePanInteraction={true}
        nodeLabel={nodeTooltip}
        d3ForceStrength={-200}
        linkDistance={120}
        warmupTicks={200}
        cooldownTicks={50}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.3}
        onEngineTick={() => {
          if (fitStage.current === 'first-tick') {
            fitStage.current = 'settled';
            showWholeGraph(graphRef.current, 0, keepZoom.current, hiddenEachSide.current);
          }
        }}
        onEngineStop={() => {
          setIsSimulationStable(true);
          if (fitStage.current) {
            fitStage.current = null;
            showWholeGraph(graphRef.current, 400, keepZoom.current, hiddenEachSide.current);
          }
        }}
        nodeCanvasObject={(node, ctx, globalScale) => {
          const focusAlpha = easeNodeAlpha(node, focusRef.current);
          // Size the node by the zoom S-curve: draw in world units, scaled about the node centre
          const scale = nodeScale(globalScale);
          ctx.save();
          ctx.translate(node.x, node.y);
          ctx.scale(scale, scale);
          ctx.translate(-node.x, -node.y);
          ctx.globalAlpha = focusAlpha;
          const nodeHovered = hoveredNode && hoveredNode.id === node.id;

          if (node.group === 'contact' && node.contactData) {
            // Cards are drawn in world units rather than on the size curve, so the name stays legible
            ctx.restore();
            ctx.save();
            ctx.globalAlpha = (shouldShowNodes ? (isSimulationStable ? 1 : 0.8) : 0) * focusAlpha;
            drawPersonCard(ctx, node, globalScale, nodeHovered, selectedNodeData?.id === node.id);
            ctx.restore();
            return;
          }

          let label = '';
          if (node.group === 'contact_count') {
            label = '';
          } else if (node.group === 'more_companies') {
            label = 'more companies';
          } else {
            label = node.name || node.id;
          }

          const getBaseNodeRadius = (nodeGroup, nodeData) => {
            switch (nodeGroup) {
              case 'contact': return 6;
              case 'company': return 16;
              case 'more_companies': return 11;
              case 'contact_count': return contactCountRadius(nodeData?.contactCountData?.count || 1);
              case 'industry': return 20;
              default: return 16;
            }
          };

          const baseNodeRadius = getBaseNodeRadius(node.group, node);
          const maxNodeRadius = node.group === 'contact_count' ? 35 :
                                node.group === 'industry' ? 30 :
                                node.group === 'company' ? 25 : 15;
          const scaledRadius = baseNodeRadius * Math.pow(globalScale, 0.4);
          const nodeRadius = Math.max(8, Math.min(maxNodeRadius, scaledRadius));
          const labelOffset = nodeRadius + Math.max(4, 10/globalScale);

          const avgCharWidth = 7;
          const estimatedTextWidth = Math.min(label.length, LABEL_WRAP_EMS * 2) * avgCharWidth;
          const labelSpacing = 15;
          const padding = 10;
          const collisionRadius = Math.max(
            estimatedTextWidth / 2 + baseNodeRadius + labelSpacing + padding,
            25
          );

          if (nodeHovered) {
            ctx.fillStyle = theme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.05)';
            ctx.beginPath();
            ctx.arc(node.x, node.y, collisionRadius, 0, 2 * Math.PI, false);
            ctx.fill();
          }

          // Multiply by the focus alpha so hover-dimming survives this fade-in
          ctx.globalAlpha = (shouldShowNodes ? (isSimulationStable ? 1 : 0.8) : 0) * focusAlpha;

          if (node.group === 'company') {
            const logoImg = companyLogos.get(node.id);

            if (logoImg) {
              const logoSize = nodeRadius * 2.2;
              try {
                ctx.save();
                ctx.beginPath();
                ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
                ctx.clip();
                ctx.drawImage(
                  logoImg,
                  node.x - logoSize/2,
                  node.y - logoSize/2,
                  logoSize,
                  logoSize
                );
                ctx.restore();

                ctx.strokeStyle = nodeHovered ?
                  (theme === 'dark' ? '#66d9aa' : '#009688') :
                  palette.border;
                ctx.lineWidth = Math.max(1, 2/Math.max(0.8, globalScale));
                ctx.beginPath();
                ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
                ctx.stroke();
              } catch (error) {
                // Fallback to colored circle if image drawing fails
                ctx.fillStyle = nodeHovered ?
                  (theme === 'dark' ? '#66d9aa' : '#009688') :
                  palette.company;
                ctx.beginPath();
                ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
                ctx.fill();
              }
            } else {
              const placeholderColor = nodeHovered ?
                (theme === 'dark' ? '#66d9aa' : '#009688') :
                palette.company;

              ctx.fillStyle = placeholderColor;
              ctx.beginPath();
              ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
              ctx.fill();

              drawIcon(ctx, COMPANY_ICON, theme === 'dark' ? '#2d2d2d' : '#ffffff', node.x, node.y, nodeRadius * 1.1);
            }
          } else if (node.group === 'industry') {
            const industryColor = theme === 'dark' ? '#5FCFB4' : '#0E7A62';

            ctx.fillStyle = industryColor;
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
            ctx.fill();

            ctx.strokeStyle = nodeHovered ? industryColor : palette.border;
            ctx.lineWidth = Math.max(1, 2/Math.max(0.8, globalScale));
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
            ctx.stroke();

            drawIcon(ctx, node.type === 'icp' ? ICP_ICON : INDUSTRY_ICON, theme === 'dark' ? '#1f2937' : '#ffffff', node.x, node.y, nodeRadius * 1.1);
          } else if (node.group === 'more_companies') {
            const radius = nodeRadius;
            ctx.fillStyle = palette.panel;
            ctx.beginPath();
            ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
            ctx.fill();

            ctx.setLineDash([3, 2]);
            ctx.strokeStyle = nodeHovered ? palette.link : palette.borderStrong;
            ctx.lineWidth = Math.max(1, 1.5/Math.max(0.8, globalScale));
            ctx.stroke();
            ctx.setLineDash([]);

            const moreText = `+${node.hiddenCount}`;
            ctx.fillStyle = palette.text;
            ctx.font = `bold ${Math.max(6, radius * (moreText.length > 4 ? 0.56 : 0.7))}px ${FONT_STACK}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(moreText, node.x, node.y);
          } else if (node.group === 'contact_count') {
            const contactCount = node.contactCountData?.count || 0;

            const contactCountColor = nodeHovered ?
              (theme === 'dark' ? '#ffb74d' : '#ff8c00') :
              '#ff9800';

            ctx.fillStyle = contactCountColor;
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
            ctx.fill();

            ctx.strokeStyle = nodeHovered ?
              (theme === 'dark' ? '#ffb74d' : '#ff6f00') :
              palette.border;
            ctx.lineWidth = Math.max(1, 2/Math.max(0.8, globalScale));
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
            ctx.stroke();

            ctx.fillStyle = theme === 'dark' ? '#1f2937' : '#ffffff';

            const maxDigits = Math.max(1, Math.floor(Math.log10(contactCount)) + 1);
            const baseFontSize = Math.max(9, nodeRadius * 0.72);
            const adjustedFontSize = maxDigits > 2 ? baseFontSize * 0.8 : baseFontSize;

            ctx.font = `bold ${adjustedFontSize}px ${FONT_STACK}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(contactCount.toString(), node.x, node.y);
          } else {
            let nodeColor = getNodeColor(node, theme);

            if (nodeHovered) {
              const rgb = nodeColor.match(/\w\w/g);
              if (rgb) {
                const [r, g, b] = rgb.map(x => parseInt(x, 16));
                const lighterR = Math.min(255, r + 40);
                const lighterG = Math.min(255, g + 40);
                const lighterB = Math.min(255, b + 40);
                nodeColor = `rgb(${lighterR}, ${lighterG}, ${lighterB})`;
              }
            }

            ctx.fillStyle = nodeColor;
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
            ctx.fill();
          }

          if (selectedNodeData && selectedNodeData.id === node.id) {
            ctx.strokeStyle = palette.link;
            ctx.lineWidth = Math.max(2, 3/Math.max(0.8, globalScale));
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeRadius + 2, 0, 2 * Math.PI, false);
            ctx.stroke();
          }

          if (label) {
            labelQueue.current.push({
              id: node.id,
              node,
              text: label,
              x: node.x,
              y: node.y + labelOffset * scale,
              alpha: focusAlpha,
              wrap: !(peopleShown && node.group === 'company'),
              priority: nodeHovered ? 3 : LABEL_PRIORITY[node.group] || 0,
              always: nodeHovered || LABEL_ALWAYS.has(node.group)
            });
          } else {
            node.__labelBox = null;
          }

          ctx.globalAlpha = 1;
          ctx.restore();
        }}
        onRenderFramePost={(ctx, globalScale) => {
          drawLabels(ctx, labelQueue.current, globalScale, palette);
          labelQueue.current = [];
        }}
        // Keep the canvas redrawing so eased hover/focus transitions animate every frame
        autoPauseRedraw={false}
        linkWidth={(link) => edgeWorldWidth(zoomRef.current, link.__highlight ?? 0)}
        linkDirectionalParticles={(link) => (link.__highlight > 0.5 ? 3 : 0)}
        linkDirectionalParticleSpeed={0.004}
        // linkColor runs once per link per frame, so it is where the highlight eases
        linkColor={(link) => {
          const focus = focusRef.current;
          const onPath = Boolean(focus && focus.nodes.has(endpointId(link.source)) && focus.nodes.has(endpointId(link.target)));
          const highlight = easeToward(link, '__highlight', onPath ? 1 : 0);
          const dim = focus ? 1 - highlight : 0;
          return withAlpha(palette.border, 1 - dim * (1 - FOCUS_LINK_DIM_ALPHA));
        }}
        linkDirectionalParticleColor={() => palette.link}
        nodeVal={nodeBaseSize}
        nodePointerAreaPaint={(node, color, ctx, globalScale) => {
          ctx.fillStyle = color;
          if (node.group === 'contact' && node.contactData) {
            const card = personCard(ctx, node, globalScale);
            ctx.fillRect(card.x, card.y, card.width, card.height);
            return;
          }
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeBaseSize(node) * nodeScale(globalScale), 0, 2 * Math.PI, false);
          ctx.fill();
          if (node.__labelBox) {
            const { left, top, right, bottom } = node.__labelBox;
            ctx.fillRect(left, top, right - left, bottom - top);
          }
        }}
        onNodeHover={(node) => {
          setHoveredNode(node);
          document.body.style.cursor = node ? 'pointer' : 'auto';
        }}
        onZoom={(transformState) => {
          if (!transformState) return;
          zoomRef.current = transformState.k;
          // The graph reports zoom while it renders; tell the parent on the next frame instead
          if (onZoomChange && zoomFrame.current === null) {
            zoomFrame.current = requestAnimationFrame(() => {
              zoomFrame.current = null;
              onZoomChange(zoomRef.current);
            });
          }
        }}
        onNodeClick={(node) => {
          const fg = graphRef.current;
          // A company opens its panel, which moves the camera itself
          if (node.group !== 'company' && fg && typeof fg.centerAt === 'function' && node.x != null) {
            fg.centerAt(node.x, node.y, 700);
          }
          onNodeClick(node);
        }}
        onBackgroundClick={() => setHoveredNode(null)}
      />
    </div>
  );
};

export default GraphView;
